// Click event — tracks button and link clicks; auto-derives location/type from the closest [data-id] ancestor

import type { SyntheticEvent } from 'react';
import { pushAdobeEvent, getCleanUrl } from './core';
import { getCurrentPagePayload } from './pageLoad';

export type ClickParams = {
  clickName: string;
  // Descriptive type like 'accordion-button' or 'header-link'; auto-derived from the click target when omitted
  clickType?: string;
  // Component identifier — auto-derived from the closest [data-id] ancestor when omitted
  clickLocation?: string;
  // Destination URL for redirection clicks; auto-derived from the closest anchor when omitted.
  // An http(s) URL is cleaned (query/hash stripped); any other string (e.g. a static marker
  // like "InPageNavigation Anchor") is sent as-is.
  clickUrl?: string;
};

// Resolves the DOM element from a React SyntheticEvent or a raw Element reference
const resolveTargetElement = (source: SyntheticEvent<Element> | Element): Element | null => {
  if (source instanceof Element) return source;
  return (source.currentTarget as Element | null) ?? null;
};

// Returns the data-id of the closest ancestor (or the element itself) that has the attribute
const getNearestDataId = (start: Element): string | null => {
  return start.closest('[data-id]')?.getAttribute('data-id') ?? null;
};

// Returns the leading underscore-separated segment of a data-id, lowercased — represents the component name
const parseComponentName = (dataId: string): string => {
  const head = dataId.split('_')[0] ?? '';
  return head.toLowerCase();
};

// Classifies an element as 'link' (anchor) or 'button' (everything else)
const getElementKind = (el: Element): 'button' | 'link' => {
  return el.tagName.toLowerCase() === 'a' ? 'link' : 'button';
};

// Per-component static clickType values, keyed by the auto-derived `${componentName}-${kind}`.
// Only remaps DERIVED types (explicit clickType always wins), so other components are untouched.
const DERIVED_CLICK_TYPE_OVERRIDES: Record<string, string> = {
  'imagevideocarousel-button': 'media-click',
  'header-button': 'header-click',
  'tabbedcontent-button': 'tabbedcontent-click',
};

// Returns the clean destination URL (domain + path, no query/hash) of the closest anchor ancestor; null when not a redirection
const getClickUrl = (start: Element): string | null => {
  // The anchor `href` *property* (unlike the attribute) is always a fully-qualified absolute URL
  const anchor = start.closest('a');
  return anchor?.href ? getCleanUrl(anchor.href) || null : null;
};

// Tracks a Click event — pass `source` (a React event or DOM element) to auto-derive clickLocation/clickType
export const trackAdobeClick = (
  params: ClickParams,
  source?: SyntheticEvent<Element> | Element | null
): void => {
  const eventName = 'Click';
  const page = getCurrentPagePayload();

  let derivedLocation: string | null = null;
  let derivedType: string | null = null;
  let derivedUrl: string | null = null;
  // Set when a per-component override applies: its static value is mirrored into clickUrl too
  let staticClickUrl: string | null = null;

  if (source) {
    const target = resolveTargetElement(source);

    if (target) {
      const nearestDataId = getNearestDataId(target);
      derivedLocation = nearestDataId;
      const kind = getElementKind(target);
      const componentName = nearestDataId ? parseComponentName(nearestDataId) : '';
      const rawDerivedType = componentName ? `${componentName}-${kind}` : kind;
      // Apply the per-component static override when one exists for this derived type
      const override = DERIVED_CLICK_TYPE_OVERRIDES[rawDerivedType];
      derivedType = override ?? rawDerivedType;
      // For overridden components, clickUrl mirrors the static clickType instead of the anchor URL
      if (override) staticClickUrl = override;
      derivedUrl = getClickUrl(target);
    }
  }

  const clickLocation = params.clickLocation ?? derivedLocation ?? 'body';
  const clickType = params.clickType ?? derivedType ?? 'link';

  // A provided http(s) URL is cleaned (query/hash stripped); any other string passes through as-is.
  const providedUrl = params.clickUrl
    ? /^https?:\/\//i.test(params.clickUrl)
      ? getCleanUrl(params.clickUrl) || params.clickUrl
      : params.clickUrl
    : '';
  // Resolve through: explicit param → component static value → derived anchor URL → clickType.
  // The clickType fallback guarantees clickUrl is never empty.
  const clickUrl = providedUrl || staticClickUrl || derivedUrl || clickType;

  pushAdobeEvent({
    event: eventName,
    eventName,
    eventDesc: `${eventName} - ${params.clickName}`,
    ...(page && { page }),
    click: {
      clickName: params.clickName,
      clickLocation,
      clickType,
      // Always present — falls back to clickType so it's never empty
      clickUrl,
    },
  });
};
